import dayjs from "dayjs"
import { toApiDate } from "../../helpers/date"
import api from "../../components/api"
import { ActualItem } from "../workareaItem"
import { ExportInvoiceData, InvoiceDetail, InvoiceSummary, InvoiceSummaryWithCustomerJobData, JobForInvoicing } from "./invoice"
import { InvoicePaymentStatus, PaymentMethodSummary } from "./payment"
import { InvoicingSystem } from "./settings/invoicingSettings"
import { StatementSummary } from "./statement"
import { Transaction } from "./transaction"

export const getInvoiceSummariesForJob = async (jobId: number): Promise<InvoiceSummary[]> => {
  return (await api.get(`/web/invoicing/job/${jobId}/invoice-summaries`)).data
}

export const getTransactionsForJob = async (jobId: number): Promise<Transaction[]> => {
  return (await api.get(`/web/invoicing/job/${jobId}/transactions`)).data
}

export const getInvoiceDetails = async (invoiceId: string): Promise<InvoiceDetail> => {
  return (await api.get(`/web/invoicing/invoice/${invoiceId}/details`)).data
}

export const emailInvoice = async (invoiceId: string): Promise<void> => {
  await api.post(`/web/invoicing/invoice/${invoiceId}/send-quickbooks-email`)
}

type ExportInvoiceResponse = {
  qboInvoiceUrl: string
}

export const exportInvoice = async (data: ExportInvoiceData): Promise<ExportInvoiceResponse> => {
  return (await api.post(`/web/invoicing/invoice/${data.invoiceId}/export-to-quickbooks`, data)).data
}

export const deleteInvoice = async (invoiceId: string, deleteFromQuickBooks: boolean): Promise<void> => {
  return await api.delete(`/web/invoicing/invoice/${invoiceId}?deleteQboInvoice=${deleteFromQuickBooks}`)
}

export type CreateInvoiceRequest = {
  invoice: CreateInvoiceData
  actualItemsToUpdate: ActualItem[]
}

type CreateInvoiceData = {
  projectId: number
  dueDateUtc: string
  enableCreditCardPayments: boolean
  enableAchPayments: boolean
  showAuthorizePaymentOnFile: boolean
  requireAuthorizePaymentOnFile: boolean
  includePaymentSummary: boolean
  invoicingSystem: InvoicingSystem
  lineItems: LineItem[]
}

type LineItem = {
  workareaId: number
  childWorkareaIds: number[]
  description: string
  name: string
  taxable: boolean
  amountWithoutTax: number
  deductDownPayment: boolean
  includeItemsSummary: boolean
  invoiceGroupingOption: number
  exemptSalesTaxId: number | null
  selectedSalesTaxId: number | null
  taxRateId: string | null
  itemIds: number[]
  laborIds: string[]
  visitIds: string[]
  amountWithoutTaxPerWorkarea?: Record<number, number> | undefined
}

export type CreateInvoiceResponse = {
  invoiceId: string
  invoicingSystem: InvoicingSystem
  qboInvoiceUrl: string
}

export const createInvoice = async (data: CreateInvoiceRequest): Promise<CreateInvoiceResponse> => {
  return (await api.post('/web/invoicing/invoice', data)).data
}

export const UNRESOLVABLE_TAX_RATE_ERROR_CODE = 'taxRateUnresolvable'

export const isUnresolvableTaxRateError = (error: any): boolean => {
  return error?.response?.data?.errorCode === UNRESOLVABLE_TAX_RATE_ERROR_CODE
}

type TaxCode = {
  id: string
  name: string
  description: string
}

export const getTaxCodes = async (): Promise<TaxCode[]> => {
  return (await api.get(`/quickbooks/tax-codes`)).data
}

export type CompletedWorkareaVisit = {
  workareaId: number
  visitId: string
  visitPrice?: number
  visitRecovery?: number | null
  isInvoiced: boolean
}

export const getCompletedWorkareaVisits = async (jobId: number): Promise<CompletedWorkareaVisit[]> => {
  return (await api.get<CompletedWorkareaVisit[]>(`/web/visits/completed-workareas/${jobId}`)).data
}

export type UpdateVisitPricingRequest = {
  jobId: number
  workareaId: number
  visitIds: string[]
  visitPrice: number
}

export const updateCompletedVisitPricing = async (data: UpdateVisitPricingRequest): Promise<void> => {
  return await api.post(`/web/visits/update-completed-pricing`, data)
}

export const getPaymentStatus = async (invoiceId: string): Promise<InvoicePaymentStatus> => {
  return (await api.get(`/web/invoicing/invoice/${invoiceId}/payment-status`)).data
}

export const getPaymentMethodsOnFile = async (customerId: number): Promise<PaymentMethodSummary[]> => {
  return (await api.get(`/web/invoicing/customer/${customerId}/payment-methods-on-file`)).data
}

type PayWithPaymentMethodOnFileRequest = {
  paymentId: string
  paymentMethodId: string
}

export const payWithPaymentMethodOnFile = async (data: PayWithPaymentMethodOnFileRequest): Promise<void> => {
  return await api.post(`/web/invoicing/pay-with-payment-method-on-file`, data)
}

export type MarkInvoicePaidManuallyRequest = {
  invoiceId: string
  paymentMethod: 'cash' | 'check'
  checkNumber?: string
}

export const markInvoicePaidManually = async (data: MarkInvoicePaidManuallyRequest): Promise<void> => {
  return await api.post(`/web/invoicing/invoice/${data.invoiceId}/manually-mark-paid`, {
    paymentMethod: data.paymentMethod,
    checkNumber: data.checkNumber
  })
}

export const markInvoiceNotPaidManually = async (invoiceId: string): Promise<void> => {
  return await api.post(`/web/invoicing/invoice/${invoiceId}/manually-mark-not-paid`)
}

export type InvoiceSendOptions = {
  dueOn?: string
  enableCreditCardPayments: boolean
  enableAchPayments: boolean
  showAuthorizePaymentOnFile: boolean
  requireAuthorizePaymentOnFile: boolean
}

export type SendInvoiceRequest = {
  invoiceId: string
  emailAddresses: string[]
  invoiceUpdates: InvoiceSendOptions
}

export const sendInvoice = async (data: SendInvoiceRequest): Promise<void> => {
  return await api.post(`/web/invoicing/invoice/${data.invoiceId}/send-email`, data)
}

export const syncInvoiceToQuickBooks = async (invoiceId: string): Promise<void> => {
  await api.post(`/web/invoicing/invoice/${invoiceId}/sync-to-quickbooks`)
}

type PaymentMethodCollectionLink = {
  link: string
}

export const getPaymentCollectionLink = async (customerId: number): Promise<PaymentMethodCollectionLink> => {
  return (await api.get(`/web/invoicing/customer/${customerId}/payment-method-collection-link`)).data
}

export type GetJobsForInvoicingRequest = {
  startDate: string | null
  endDate: string | null
}

export const getJobsForInvoicing = async (request: GetJobsForInvoicingRequest): Promise<JobForInvoicing[]> => {
  const params = (request.startDate && request.endDate)
    ? {
      startDate: dayjs(request.startDate).toISOString().substring(0, 10),
      endDate: dayjs(request.endDate).toISOString().substring(0, 10)
    }
    : undefined
  return (await api.get('/billing/projects', { params })).data
}

export type GetReadyToInvoiceJobsRequest = {
  startDate: string | null
  endDate: string | null
  divisionGroups?: string[]
  workAreaStatuses?: number[]
  jobTypes?: number[]
  visitReviewStatuses?: number[]
  search?: string
}

export type ReadyToInvoiceResponse = {
  jobs: JobForInvoicing[]
  defaultDueInDays: number
}

export const getReadyToInvoiceJobs = async (request: GetReadyToInvoiceJobsRequest): Promise<ReadyToInvoiceResponse> => {
  const params: Record<string, string | string[] | number[]> = {}
  if (request.startDate && request.endDate) {
    params.startDate = toApiDate(request.startDate)
    params.endDate = toApiDate(request.endDate)
  }
  if (request.divisionGroups !== undefined) {
    params.divisionGroups = request.divisionGroups.length > 0 ? request.divisionGroups : ['']
  }
  if (request.workAreaStatuses !== undefined) {
    params.workAreaStatuses = request.workAreaStatuses.length > 0 ? request.workAreaStatuses : ['']
  }
  if (request.jobTypes !== undefined) {
    params.jobTypes = request.jobTypes.length > 0 ? request.jobTypes : ['']
  }
  if (request.visitReviewStatuses !== undefined) {
    params.visitReviewStatuses = request.visitReviewStatuses.length > 0 ? request.visitReviewStatuses : ['']
  }
  const search = request.search?.trim()
  if (search) {
    params.search = search
  }
  const hasParams = Object.keys(params).length > 0
  return (await api.get('/web/invoicing/ready-to-batch-invoice', {
    params: hasParams ? params : undefined,
    paramsSerializer: { indexes: null }
  })).data
}

export type AllInvoicesRequest = {
  page: number
  pageSize: number
  sortBy: 'CreatedAt' | 'DueOn' | 'JobName' | 'CustomerName'
  sortDescending: boolean
  emailFilter: {
    sent: boolean,
    notSent: boolean
  }
  paidStatus?: 'paid' | 'not-paid'
  search?: string
  customerId?: number
  jobType?: number
  divisionId?: number
  qboStatus?: 'synced' | 'not-synced'
  creationDateStart?: string
  creationDateEnd?: string
  dueDateStart?: string
  dueDateEnd?: string
}

export type InvoiceListRowRef = {
  type: 'invoice' | 'statement'
  id: string
}

export type AllInvoicesResponse = {
  summaryCount: number
  totalUnpaid: number
  totalOverdue: number
  totalPaid: number
  totalInvoicedCents: number
  sentCount: number
  summaries: InvoiceSummaryWithCustomerJobData[]
  statements: StatementSummary[]
  rows?: InvoiceListRowRef[]
}

export const getAllInvoiceSummaries = async (request: AllInvoicesRequest): Promise<AllInvoicesResponse> => {
  return (await api.get('/web/invoicing/all-invoice-summaries', { params: request })).data
}

export type AllInvoiceIdsRequest = Omit<AllInvoicesRequest, 'page' | 'pageSize' | 'sortBy' | 'sortDescending'>

export type AllInvoiceIdsResponse = {
  invoiceIds: string[]
  statementIds: string[]
}

export const getAllInvoiceIds = async (request: AllInvoiceIdsRequest): Promise<AllInvoiceIdsResponse> => {
  return (await api.get('/web/invoicing/all-invoice-ids', { params: request })).data
}

export type BatchCreateRequest = {
  startDate?: string
  cutOffDate?: string
  divisionGroups?: string[]
  workAreaStatuses?: number[]
  jobIds: number[]
  dueDate: string
}

export type BatchCreateResponse = {
  batchId: string
  totalJobs: number
}

export type BatchStatus = {
  batchId: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  startDate?: string | null
  cutOffDate?: string | null
  divisionGroups?: string[] | null
  workAreaStatuses?: number[] | null
  totalJobs: number
  completedJobs: number
  skippedJobs: number
  failedJobs: number
  jobs: BatchJobStatus[]
}

export type BatchJobStatus = {
  id: string
  jobId: number
  jobName: string
  customerName: string
  status: 'not-started' | 'in-progress' | 'completed' | 'skipped' | 'failed'
  invoiceId?: string
  statementId?: string
  message: string
  messageType: 'error' | 'warning' | 'info' | ''
}

export type BatchType = 'create' | 'send' | 'delete' | 'sync'

export type ActiveBatchResponse = {
  batchId: string
  status: string
  totalJobs: number
  completedJobs: number
  failedJobs: number
  batchType: BatchType
}

export const batchCreateInvoices = async (data: BatchCreateRequest): Promise<BatchCreateResponse> => {
  return (await api.post('/web/invoicing/batch-create', data)).data
}

export const getBatchStatus = async (batchId: string): Promise<BatchStatus> => {
  return (await api.get(`/web/invoicing/batches/${batchId}/status`)).data
}

export const getActiveBatch = async (): Promise<ActiveBatchResponse | null> => {
  const response = await api.get('/web/invoicing/batches/active')
  return response.status === 204 ? null : response.data
}

export type BatchHistoryItem = {
  batchId: string
  startDate?: string | null
  cutOffDate: string | null
  divisionGroups?: string[] | null
  workAreaStatuses?: number[] | null
  requestedByUserName: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  totalJobs: number
  completedJobs: number
  skippedJobs: number
  failedJobs: number
  createdAt: string
  completedAt: string | null
  batchType: BatchType
}

export type BatchHistoryResponse = {
  items: BatchHistoryItem[]
  totalCount: number
}

export type BatchHistoryRequest = {
  page: number
  pageSize: number
}

export const getBatchHistory = async (request: BatchHistoryRequest): Promise<BatchHistoryResponse> => {
  return (await api.get('/web/invoicing/batches/history', { params: request })).data
}

export const getBatchJobs = async (batchId: string): Promise<BatchJobStatus[]> => {
  return (await api.get(`/web/invoicing/batches/${batchId}/jobs`)).data
}

export type BulkSendRequest = {
  invoiceIds: string[]
  statementIds: string[]
  invoiceUpdates: InvoiceSendOptions
}

export const bulkSendInvoices = async (data: BulkSendRequest): Promise<BatchCreateResponse> => {
  return (await api.post('/web/invoicing/bulk-send', data)).data
}

export type BulkDeleteRequest = {
  invoiceIds: string[]
  statementIds: string[]
  deleteFromQuickBooks: boolean
}

export const bulkDeleteInvoices = async (data: BulkDeleteRequest): Promise<BatchCreateResponse> => {
  return (await api.post('/web/invoicing/bulk-delete', data)).data
}

export type BulkSyncRequest = {
  invoiceIds: string[]
  statementIds: string[]
}

export const bulkSyncInvoices = async (data: BulkSyncRequest): Promise<BatchCreateResponse> => {
  return (await api.post('/web/invoicing/bulk-sync', data)).data
}
